import { Capacitor } from '@capacitor/core';

/**
 * Nettverksstatus for datasparing.
 *
 * Prefetch/aggressiv caching skal KUN skje på umålt nett (wifi/ethernet),
 * aldri på mobildata. Vi bruker Network Information API (`navigator.connection`)
 * der den finnes:
 *   - Web (Chrome/Edge) og Android WebView (Chromium) → nøyaktig `type`.
 *   - iOS WKWebView og Safari/Firefox desktop → API-et mangler.
 *
 * Fallback-policy når vi ikke VET:
 *   - Native uten info (typisk iOS) → behandle som MÅLT (returner false) for å
 *     aldri risikere å brenne mobildata. iOS-prefetch kan aktiveres senere ved
 *     å installere @capacitor/network.
 *   - Web uten info (desktop) → behandle som umålt (returner true); desktop er
 *     sjelden på målt forbindelse, og ambient-cache-gevinsten er verdt det.
 */

type NavConnection = {
  type?: string;
  effectiveType?: string;
  saveData?: boolean;
};

function getConnection(): NavConnection | undefined {
  const nav = globalThis.navigator as unknown as {
    connection?: NavConnection;
    mozConnection?: NavConnection;
    webkitConnection?: NavConnection;
  } | undefined;
  if (!nav) return undefined;
  return nav.connection || nav.mozConnection || nav.webkitConnection;
}

/**
 * Returnerer true kun når vi er rimelig sikre på at forbindelsen er umålt.
 */
export function isUnmetered(): boolean {
  const conn = getConnection();

  if (conn) {
    // Brukeren har bedt om datasparing → alltid behandle som målt.
    if (conn.saveData === true) return false;
    if (conn.type === 'cellular') return false;
    if (conn.type === 'wifi' || conn.type === 'ethernet') return true;
    // `type` finnes men er 'unknown'/'none'/tom → fall gjennom til plattform-policy.
  }

  // Ingen (pålitelig) info: konservativt på native, optimistisk på web.
  return !Capacitor.isNativePlatform();
}

/** Motsatt av isUnmetered — praktisk for lesbarhet på kallstedet. */
export function isMetered(): boolean {
  return !isUnmetered();
}
